import { useState, useEffect, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Command, CommandGroup, CommandItem } from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';

interface Tag {
  id: string;
  name: string;
}

interface TagInputProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
}

export function TagInput({ selectedTags, onTagsChange }: TagInputProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  useEffect(() => {
    if (currentWorkspace) {
      fetchTags();
    }
  }, [currentWorkspace?.id]);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('template_tag_stats')
        .select('id, name, template_count')
        .eq('workspace_id', currentWorkspace?.id)
        .order('name');

      if (error) throw error;
      setAvailableTags(data.map(({ id, name }) => ({ id, name })));
      setTagCounts(data.reduce((acc, { id, template_count }) => ({
        ...acc,
        [id]: template_count
      }), {}));
    } catch (error) {
      console.error('Error fetching tags:', error);
      captureError(error as Error, { feature: 'templates-tag-input-fetch-tags' });
      toast({
        title: "Error",
        description: "Failed to load tags",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createTag = async (name: string) => {
    if (!name.trim()) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('template_tags')
        .insert({
          name: name.toLowerCase().trim(),
          workspace_id: currentWorkspace?.id,
        })
        .select()
        .single();

      if (error) throw error;
      
      setAvailableTags(prev => [...prev, data]);
      onTagsChange([...selectedTags, data]);
      setInputValue('');
      setOpen(false);
    } catch (error) {
      console.error('Error creating tag:', error);
      captureError(error as Error, { feature: 'templates-tag-input-create-tag' });
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const removeTag = (tagToRemove: Tag) => {
    onTagsChange(selectedTags.filter(tag => tag.id !== tagToRemove.id));
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      
      const exactMatch = availableTags.find(
        tag => tag.name.toLowerCase() === inputValue.toLowerCase().trim()
      );

      if (exactMatch) {
        if (!selectedTags.some(tag => tag.id === exactMatch.id)) {
          onTagsChange([...selectedTags, exactMatch]);
        }
      } else if (inputValue.trim()) {
        await createTag(inputValue);
      }
      
      setInputValue('');
    }
  };

  const filteredTags = availableTags.filter(tag => 
    !selectedTags.some(selected => selected.id === tag.id) &&
    tag.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {/* Input field container */}
      <div className="relative">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full"
          placeholder="Type interest and press Enter"
          onClick={() => setOpen(true)}
        />

        {/* Dropdown suggestions */}
        {open && (
          <Command className="absolute z-50 w-full mt-2 rounded-md border bg-popover shadow-md">
            <ScrollArea className="max-h-[200px]">
              <CommandGroup>
                {loading ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Loading...
                  </div>
                ) : (
                  <>
                    {filteredTags.length > 0 ? (
                      filteredTags.map(tag => (
                        <CommandItem
                          key={tag.id}
                          onSelect={() => {
                            onTagsChange([...selectedTags, tag]);
                            setInputValue('');
                            setOpen(false);
                          }}
                          className="flex items-center justify-between"
                        >
                          <span>{tag.name}</span>
                          {tagCounts[tag.id] > 0 && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {tagCounts[tag.id]}
                            </Badge>
                          )}
                        </CommandItem>
                      ))
                    ) : inputValue.trim() && (
                      <CommandItem
                        onSelect={() => createTag(inputValue)}
                        className="text-primary"
                      >
                        Create "{inputValue.trim()}"
                      </CommandItem>
                    )}
                  </>
                )}
              </CommandGroup>
            </ScrollArea>
          </Command>
        )}
      </div>

      {/* Selected tags container */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map(tag => (
            <Badge 
              key={tag.id}
              variant="secondary"
              className="flex items-center gap-1 bg-muted hover:bg-muted/80"
            >
              {tag.name}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}